import { useEffect, useState } from 'react';
import { X, ExternalLink, Download, Loader2, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { reportError } from '../../../lib/errors';

interface ReceiptModalProps {
    url: string | null;
    onClose: () => void;
    vendor: string;
}

/**
 * Why the receipt is kept at all: HMRC wants the evidence behind a claimed
 * expense retained for five years (six for a company), and a photo sitting in
 * a phone's camera roll is not a record — it goes when the phone does. The
 * upload IS the filing cabinet, so this is where you come to check what was
 * actually on the paper.
 *
 * Two things were broken here, both of which made it look like the file had
 * not been stored:
 *
 *  - PDFs were rendered in an <iframe>. Chrome on Android has no built-in PDF
 *    viewer for framed content and shows a broken-document glyph on a grey
 *    field — which is exactly what a missing file looks like. Images now use
 *    <img> (which works everywhere), and a PDF on a device without a framed
 *    viewer gets an honest "open it" card instead of a fake preview.
 *
 *  - "Download" was an <a download> pointing at Supabase storage. The download
 *    attribute is IGNORED for a cross-origin href, so the browser navigated to
 *    it instead — "it opens a new window rather than downloading". The file is
 *    now fetched to a blob and saved from a same-origin blob: URL, where the
 *    attribute is honoured.
 */
const isImageUrl = (url: string) => /\.(png|jpe?g|webp|gif|avif|heic)(\?|$)/i.test(url);
const isPdfUrl = (url: string) => /\.pdf(\?|$)/i.test(url);

/**
 * A storage path with no extension — the OCR pipeline writes some of these.
 * Treat it as an image and let onError fall through, rather than guessing PDF
 * and showing the "cannot preview" card for something that would have rendered.
 */
const looksLikeStorage = (url: string) =>
    url.includes('/storage/v1/object/') || url.includes('supabase.co/storage');

const filenameFrom = (url: string, vendor: string): string => {
    try {
        const path = new URL(url, window.location.origin).pathname;
        const last = decodeURIComponent(path.split('/').pop() || '');
        if (last && /\.[a-z0-9]{2,5}$/i.test(last)) return last;
    } catch {
        /* fall through to the vendor-derived name */
    }
    const ext = isPdfUrl(url) ? 'pdf' : isImageUrl(url) ? url.split('.').pop()!.split('?')[0] : 'jpg';
    return `Receipt_${(vendor || 'expense').replace(/[^a-z0-9]+/gi, '_')}.${ext}`;
};

export function ReceiptModal({ url, onClose, vendor }: ReceiptModalProps) {
    // Which url failed to render, rather than a boolean reset from an effect —
    // opening a different receipt then re-tries on its own, with no extra pass.
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    // Escape closes, like every other overlay in the app.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!url) return null;

    const imageFailed = failedUrl === url;
    const showAsImage = !imageFailed && (isImageUrl(url) || (!isPdfUrl(url) && looksLikeStorage(url)));

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Storage returned ${res.status}`);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filenameFrom(url, vendor);
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Give the browser a beat to start the save before revoking.
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
        } catch (err) {
            reportError(err, 'receipt-modal.download');
            toast.error("Couldn't download the receipt. Try Open instead.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden
                px-3 sm:px-4
                pt-[max(1rem,env(safe-area-inset-top))]
                pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-6"
        >
            <div
                className="fixed inset-0 bg-scrim/70 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div className="relative bg-card border border-line-strong/60 rounded-xl shadow-2xl w-full max-w-4xl max-h-full flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden">

                {/* Header. Wraps on a phone: title on its own line, then the
                    actions — three buttons and a vendor name never fit across
                    320px in one row, and the close button was the casualty. */}
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b border-line shrink-0">
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-widest text-fg-4 mb-0.5">Receipt</p>
                        <h3 className="text-base font-semibold text-fg truncate">{vendor}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="order-first sm:order-last shrink-0 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-fg-3 hover:text-fg bg-raised/50 hover:bg-pressed rounded-full transition-colors"
                        aria-label="Close receipt"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div className="w-full sm:w-auto order-last sm:order-none flex items-center gap-2">
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 min-h-[44px] bg-raised hover:bg-pressed text-fg-2 hover:text-fg rounded-md transition-colors text-sm font-medium border border-line-strong"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Open
                        </a>
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={downloading}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-success-fill hover:bg-success-fill disabled:opacity-60 text-on-fill rounded-md transition-colors text-sm font-semibold shadow-sm"
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            {downloading ? 'Saving…' : 'Download'}
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 w-full bg-canvas/50 overflow-auto">
                    {showAsImage ? (
                        <img
                            src={url}
                            alt={`Receipt from ${vendor}`}
                            onError={() => setFailedUrl(url)}
                            className="w-full h-auto object-contain bg-raised"
                        />
                    ) : (
                        /* A PDF, or an image that would not load. No fake
                           preview — say what it is and hand over the two things
                           that actually work. */
                        <div className="flex flex-col items-center justify-center gap-5 p-8 min-h-[320px] text-center">
                            <div className="p-5 bg-raised border border-line-strong rounded-full">
                                <FileText className="w-10 h-10 text-fg-3" />
                            </div>
                            <div className="max-w-xs">
                                <h4 className="text-fg font-semibold text-base mb-2">
                                    {imageFailed ? 'Preview unavailable' : 'PDF receipt'}
                                </h4>
                                <p className="text-fg-3 text-sm leading-relaxed">
                                    {imageFailed
                                        ? "The file is stored, but this browser couldn't render it here. Open or download it to view it."
                                        : 'Phones generally will not show a PDF inside a page. Open it in a new tab, or download it to keep a copy.'}
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 min-h-[44px] bg-pressed hover:bg-pressed text-fg rounded-lg text-sm font-medium transition-colors border border-line-glow"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Open
                                </a>
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 min-h-[44px] bg-success-fill hover:bg-success-fill disabled:opacity-60 text-on-fill rounded-lg text-sm font-semibold transition-colors shadow"
                                >
                                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                    {downloading ? 'Saving…' : 'Download'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
